import { JobProductSlug, JobStatusLabel } from 'services/jobs/jobs.model';
import { productSetupIdOf } from './mock-context.const';

/**
 * Roster fixo de jobs mock. É a fonte de verdade de IDs e status compartilhada por
 * todos os domínios: a caixa de entrada (mocks/jobs), a revisão da Contestação
 * (mocks/contestacao) e o laudo do Basilisk (mocks/basilisk) usam os MESMOS ids.
 *
 * Jobs criados pela UI durante a demo recebem ids novos e começam em "Em processamento";
 * depois de alguns segundos o mock de jobs muda o status e emite no MOCK_JOB_STREAM.
 */
export interface MockJobRosterEntry {
	id: string;
	product: JobProductSlug;
	productSetupId: string;
	status: JobStatusLabel;
	/** Descrição exibida na caixa de entrada */
	description: string;
	/** Dias atrás em que o job foi criado (as datas são relativas a "agora") */
	createdDaysAgo: number;
	/** id de MOCK_PEOPLE */
	createdBy: 'admin' | 'gerente' | 'operador' | 'juliana';
	source: 'UI' | 'API_KEY';
}

const CADASTRO = productSetupIdOf('trabalhista', 'cadastro');
const CONTESTACAO = productSetupIdOf('trabalhista', 'contestacao');
const BASILISK = productSetupIdOf('trabalhista', 'basilisk');

/** Jobs escritos à mão: têm dados próprios em mocks/jobs, mocks/contestacao e mocks/basilisk */
const HANDWRITTEN_JOBS: MockJobRosterEntry[] = [
	// ── Cadastro ─────────────────────────────────────────────────────────────
	{
		id: 'job-cd-001',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Finalizado',
		description: 'Ficha de admissão — João Pereira da Silva',
		createdDaysAgo: 0,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-cd-002',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Em processamento',
		description: 'CTPS e contrato — Mariana Souza',
		createdDaysAgo: 0,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-cd-003',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Aguardando',
		description: 'Lote API — 12 documentos admissionais',
		createdDaysAgo: 0,
		createdBy: 'admin',
		source: 'API_KEY',
	},
	{
		id: 'job-cd-004',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Finalizado',
		description: 'Rescisão — Carlos Henrique Almeida',
		createdDaysAgo: 1,
		createdBy: 'gerente',
		source: 'UI',
	},
	{
		id: 'job-cd-005',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Falha',
		description: 'Holerites digitalizados (baixa resolução)',
		createdDaysAgo: 2,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-cd-006',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Finalizado',
		description: 'Ficha de admissão — Beatriz Mendonça',
		createdDaysAgo: 4,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-cd-007',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Finalizado',
		description: 'Lote API — atestados médicos',
		createdDaysAgo: 9,
		createdBy: 'admin',
		source: 'API_KEY',
	},
	{
		id: 'job-cd-008',
		product: 'cadastro',
		productSetupId: CADASTRO,
		status: 'Finalizado',
		description: 'Contrato de experiência — Rafael Lima',
		createdDaysAgo: 21,
		createdBy: 'gerente',
		source: 'UI',
	},

	// ── Contestação ──────────────────────────────────────────────────────────
	{
		id: 'job-ct-001',
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		status: 'Aguardando revisão',
		description: 'Reclamação trabalhista — horas extras e adicional noturno',
		createdDaysAgo: 0,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-ct-002',
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		status: 'Em revisão',
		description: 'Rescisão indireta — assédio moral',
		createdDaysAgo: 1,
		createdBy: 'gerente',
		source: 'UI',
	},
	{
		id: 'job-ct-003',
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		status: 'Finalizado',
		description: 'Equiparação salarial — analista financeiro',
		createdDaysAgo: 3,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-ct-004',
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		status: 'Em processamento',
		description: 'Acidente de trabalho — indenização por danos morais',
		createdDaysAgo: 0,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-ct-005',
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		status: 'Falha',
		description: 'Vínculo empregatício — PJ',
		createdDaysAgo: 2,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-ct-006',
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		status: 'Aguardando',
		description: 'Lote API — insalubridade',
		createdDaysAgo: 0,
		createdBy: 'admin',
		source: 'API_KEY',
	},
	{
		id: 'job-ct-007',
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		status: 'Finalizado',
		description: 'Verbas rescisórias e multa do art. 477',
		createdDaysAgo: 12,
		createdBy: 'gerente',
		source: 'UI',
	},

	// ── Basilisk ─────────────────────────────────────────────────────────────
	{
		id: 'job-bs-001',
		product: 'basilisk',
		productSetupId: BASILISK,
		status: 'Aguardando revisão',
		description: 'atestado-medico-0423.pdf',
		createdDaysAgo: 0,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-bs-002',
		product: 'basilisk',
		productSetupId: BASILISK,
		status: 'Em revisão',
		description: 'comprovante-residencia.pdf',
		createdDaysAgo: 1,
		createdBy: 'gerente',
		source: 'UI',
	},
	{
		id: 'job-bs-003',
		product: 'basilisk',
		productSetupId: BASILISK,
		status: 'Finalizado',
		description: 'holerite-marco-2026.pdf',
		createdDaysAgo: 3,
		createdBy: 'operador',
		source: 'UI',
	},
	{
		id: 'job-bs-004',
		product: 'basilisk',
		productSetupId: BASILISK,
		status: 'Em processamento',
		description: 'ctps-digital.pdf',
		createdDaysAgo: 0,
		createdBy: 'operador',
		source: 'API_KEY',
	},
	{
		id: 'job-bs-005',
		product: 'basilisk',
		productSetupId: BASILISK,
		status: 'Falha',
		description: 'foto-documento-borrada.pdf',
		createdDaysAgo: 2,
		createdBy: 'operador',
		source: 'UI',
	},
];

// ── Geração determinística do histórico ─────────────────────────────────────

/** Gerador pseudoaleatório com semente (mesma sequência a cada carga da página) */
export interface SeededRandom {
	/** Número em [0, 1) */
	next(): number;
	/** Inteiro em [min, max] */
	int(min: number, max: number): number;
	pick<T>(items: readonly T[]): T;
	chance(probability: number): boolean;
}

/** FNV-1a de 32 bits — o MESMO hash de mocks/basilisk (escolha do cenário forense por id) */
export function hashSeed(text: string): number {
	let hash = 2166136261;
	for (let index = 0; index < text.length; index++) {
		hash ^= text.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
}

/** mulberry32 */
export function seededRandom(seed: number): SeededRandom {
	let state = seed >>> 0;
	const next = (): number => {
		state = (state + 0x6d2b79f5) >>> 0;
		let value = state;
		value = Math.imul(value ^ (value >>> 15), value | 1);
		value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
		return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
	};
	return {
		next,
		int: (min, max) => min + Math.floor(next() * (max - min + 1)),
		pick: (items) => items[Math.floor(next() * items.length)],
		chance: (probability) => next() < probability,
	};
}

const FIRST_NAMES = [
	'Adriana',
	'Alexandre',
	'Aline',
	'Bruno',
	'Camila',
	'Daniel',
	'Débora',
	'Eduardo',
	'Fabiana',
	'Felipe',
	'Fernanda',
	'Gabriel',
	'Gustavo',
	'Isabela',
	'Leandro',
	'Letícia',
	'Lucas',
	'Luana',
	'Marcelo',
	'Natália',
	'Otávio',
	'Priscila',
	'Renata',
	'Ricardo',
	'Sabrina',
	'Samuel',
	'Tatiane',
	'Vanessa',
	'Vinícius',
	'Wagner',
] as const;

const SURNAMES = [
	'Albuquerque',
	'Alves',
	'Barros',
	'Cardoso',
	'Castro',
	'Correia',
	'Dias',
	'Duarte',
	'Farias',
	'Freitas',
	'Gonçalves',
	'Machado',
	'Medeiros',
	'Monteiro',
	'Moreira',
	'Nogueira',
	'Pacheco',
	'Peixoto',
	'Pinheiro',
	'Queiroz',
	'Rezende',
	'Sampaio',
	'Siqueira',
	'Tavares',
	'Vasconcelos',
	'Vieira',
] as const;

/** Nome fictício (combinação aleatória de prenome + dois sobrenomes) */
export function fictitiousName(random: SeededRandom): string {
	const first = random.pick(SURNAMES);
	let second = random.pick(SURNAMES);
	if (second === first) second = random.pick(SURNAMES);
	return `${random.pick(FIRST_NAMES)} ${first} ${second}`;
}

type MockPersonKey = MockJobRosterEntry['createdBy'];
type MockJobSource = MockJobRosterEntry['source'];

/** Autores de jobs enviados pela tela (repetição = frequência); pela API, o dono da chave é o admin */
const UI_AUTHORS: readonly MockPersonKey[] = [
	'operador',
	'operador',
	'operador',
	'operador',
	'juliana',
	'juliana',
	'juliana',
	'gerente',
	'gerente',
	'admin',
];

interface GeneratedDescription {
	description: string;
	source: MockJobSource;
}

interface WeightedKind {
	weight: number;
	build: (random: SeededRandom, id: string) => GeneratedDescription;
}

function pickWeighted(random: SeededRandom, kinds: readonly WeightedKind[]): WeightedKind {
	const total = kinds.reduce((sum, kind) => sum + kind.weight, 0);
	let target = random.next() * total;
	for (const kind of kinds) {
		target -= kind.weight;
		if (target < 0) return kind;
	}
	return kinds[kinds.length - 1];
}

function sentByUi(description: string): GeneratedDescription {
	return { description, source: 'UI' };
}

function sentByApi(description: string): GeneratedDescription {
	return { description, source: 'API_KEY' };
}

/**
 * Tipos de envio do Cadastro. O prefixo da descrição identifica o tipo de documento
 * (mocks/jobs monta os arquivos e o resultado da extração a partir dele).
 */
const CADASTRO_KINDS: WeightedKind[] = [
	{ weight: 6, build: (random) => sentByUi(`Ficha de admissão — ${fictitiousName(random)}`) },
	{ weight: 2, build: (random) => sentByUi(`CTPS e contrato — ${fictitiousName(random)}`) },
	{ weight: 3, build: (random) => sentByUi(`Rescisão — ${fictitiousName(random)}`) },
	{
		weight: 2,
		build: (random) => sentByUi(`Holerites (3 competências) — ${fictitiousName(random)}`),
	},
	{
		weight: 2,
		build: (random) => sentByUi(`Contrato de experiência — ${fictitiousName(random)}`),
	},
	{
		weight: 1,
		build: (random) => sentByUi(`Aviso e recibo de férias — ${fictitiousName(random)}`),
	},
	{ weight: 1, build: (random) => sentByUi(`Cartões de ponto — ${fictitiousName(random)}`) },
	{ weight: 1, build: (random) => sentByUi(`Atestado médico — ${fictitiousName(random)}`) },
	{
		weight: 3,
		build: (random) => sentByApi(`Lote API — ${random.int(8, 36)} documentos admissionais`),
	},
	{
		weight: 1,
		build: (random) => sentByApi(`Lote API — ${random.int(6, 18)} atestados médicos`),
	},
	{
		weight: 1,
		build: (random) => sentByApi(`Lote API — holerites de ${random.int(20, 60)} colaboradores`),
	},
];

const CONTESTACAO_THEMES = [
	'Horas extras e intervalo intrajornada — agente de aeroporto',
	'Adicional de periculosidade — mecânico de aeronaves',
	'Adicional de insalubridade — auxiliar de limpeza',
	'Rescisão indireta — atraso reiterado de salários',
	'Equiparação salarial — analista de planejamento',
	'Dano moral — revista em armários',
	'Doença ocupacional — LER/DORT',
	'Verbas rescisórias e multa do art. 477',
	'Vínculo empregatício — motorista terceirizado',
	'Acúmulo de função — operador de check-in',
	'Sobreaviso e horas de espera — tripulante',
	'Estabilidade gestante — pedido de reintegração',
	'Diferenças de FGTS e multa de 40%',
	'Adicional noturno e hora reduzida — auxiliar de rampa',
	'Assédio moral — cobrança de metas',
	'Reversão de justa causa — abandono de emprego',
	'Intervalo interjornada — comissária de bordo',
	'Responsabilidade subsidiária — terceirização de limpeza',
	'Horas extras — trabalho externo sem controle de jornada',
	'Acidente de trajeto — estabilidade acidentária',
] as const;

const CONTESTACAO_KINDS: WeightedKind[] = [
	{ weight: 4, build: (random) => sentByUi(random.pick(CONTESTACAO_THEMES)) },
	{
		weight: 1,
		build: (random) =>
			sentByApi(`Lote API — ${random.pick(CONTESTACAO_THEMES).split(' — ')[0].toLowerCase()}`),
	},
];

/**
 * Arquivos analisados pelo Basilisk, na MESMA ordem de `BASILISK_DEFAULT_SCENARIOS`
 * (mocks/basilisk): o nome do arquivo combina com o cenário forense sorteado pelo id.
 */
const BASILISK_FILENAMES: readonly (readonly string[])[] = [
	[
		'declaracao-jornada-reclamante.pdf',
		'termo-acordo-banco-de-horas.pdf',
		'recibo-quitacao-verbas.pdf',
		'declaracao-testemunha-assinada.pdf',
	],
	['atestado-medico-{n}.pdf', 'atestado-afastamento-{n}.pdf', 'declaracao-comparecimento-{n}.pdf'],
	['comprovante-residencia-{n}.pdf', 'conta-energia-{n}.pdf', 'fatura-agua-{n}.pdf'],
	['holerite-{m}-2026.pdf', 'recibo-pagamento-{m}-2026.pdf', 'contracheque-{m}-2026.pdf'],
	['ctps-digitalizada-{n}.pdf', 'recibo-ferias-digitalizado-{n}.pdf', 'aviso-previo-scan-{n}.pdf'],
];

const MONTH_SLUGS = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 'julho', 'agosto'];

function basiliskFilename(random: SeededRandom, id: string): GeneratedDescription {
	const pool = BASILISK_FILENAMES[hashSeed(id) % BASILISK_FILENAMES.length];
	const description = random
		.pick(pool)
		.replace('{n}', String(random.int(100, 9999)).padStart(4, '0'))
		.replace('{m}', random.pick(MONTH_SLUGS));
	return random.chance(0.3) ? sentByApi(description) : sentByUi(description);
}

const BASILISK_KINDS: WeightedKind[] = [{ weight: 1, build: basiliskFilename }];

interface RosterPlan {
	product: JobProductSlug;
	productSetupId: string;
	idPrefix: string;
	/** Número do primeiro id gerado (continua a numeração dos jobs escritos à mão) */
	firstNumber: number;
	/** Jobs gerados por janela de 30 dias: [1–29, 30–59, 60–89] dias atrás */
	perWindow: readonly [number, number, number];
	/** Status dos jobs gerados mais recentes (do mais novo para o mais antigo); os demais terminaram */
	recentStatuses: readonly JobStatusLabel[];
	/** A cada quantos jobs um termina em "Falha" (espaçado, para não concentrar falhas num período) */
	failureEvery: number;
	kinds: readonly WeightedKind[];
	/** Diferencia a semente de setups do mesmo produto (outras empresas), para não repetir descrições */
	seedSuffix?: string;
}

const WINDOWS: readonly (readonly [number, number])[] = [
	[1, 29],
	[30, 59],
	[60, 89],
];

function generateJobs(plan: RosterPlan): MockJobRosterEntry[] {
	const random = seededRandom(hashSeed(`roster-${plan.product}${plan.seedSuffix ?? ''}`));
	const days = WINDOWS.flatMap(([fromDay, toDay], windowIndex) =>
		Array.from({ length: plan.perWindow[windowIndex] }, () => random.int(fromDay, toDay)),
	).sort((first, second) => first - second);

	return days.map((createdDaysAgo, index) => {
		const id = `${plan.idPrefix}-${String(plan.firstNumber + index).padStart(3, '0')}`;
		const { description, source } = pickWeighted(random, plan.kinds).build(random, id);
		const failed = index % plan.failureEvery === Math.floor(plan.failureEvery / 2);
		const status: JobStatusLabel = plan.recentStatuses[index] ?? (failed ? 'Falha' : 'Finalizado');
		return {
			id,
			product: plan.product,
			productSetupId: plan.productSetupId,
			status,
			description,
			createdDaysAgo,
			createdBy: source === 'API_KEY' ? 'admin' : random.pick(UI_AUTHORS),
			source,
		};
	});
}

/**
 * Histórico dos últimos 90 dias, com volume levemente crescente: a janela atual de 30 dias
 * e a anterior têm dados, então os dashboards mostram variações moderadas.
 * Somando os jobs escritos à mão: Cadastro 60 · Contestação 43 · Basilisk 33.
 */
const GENERATED_JOBS: MockJobRosterEntry[] = [
	...generateJobs({
		product: 'cadastro',
		productSetupId: CADASTRO,
		idPrefix: 'job-cd',
		firstNumber: 9,
		perWindow: [17, 20, 15],
		recentStatuses: [],
		failureEvery: 17,
		kinds: CADASTRO_KINDS,
	}),
	...generateJobs({
		product: 'contestacao',
		productSetupId: CONTESTACAO,
		idPrefix: 'job-ct',
		firstNumber: 8,
		perWindow: [11, 14, 11],
		recentStatuses: ['Aguardando revisão', 'Em revisão'],
		failureEvery: 12,
		kinds: CONTESTACAO_KINDS,
	}),
	...generateJobs({
		product: 'basilisk',
		productSetupId: BASILISK,
		idPrefix: 'job-bs',
		firstNumber: 6,
		perWindow: [9, 11, 8],
		recentStatuses: ['Aguardando revisão'],
		failureEvery: 12,
		kinds: BASILISK_KINDS,
	}),
];

/**
 * Jobs das outras empresas do seletor de contexto (Grupo Aurora Varejo e Telecom Pampa — ver
 * `mocks/session/session.mock.ts`). Os ids de setup seguem `clientSetupIdOf` de `mocks/context`.
 */
const OTHER_CLIENTS_JOBS: MockJobRosterEntry[] = [
	...generateJobs({
		product: 'cadastro',
		productSetupId: 'ps-aurora-civel-cadastro',
		idPrefix: 'job-aur-cd',
		firstNumber: 1,
		perWindow: [9, 8, 6],
		recentStatuses: ['Em processamento'],
		failureEvery: 11,
		kinds: CADASTRO_KINDS,
		seedSuffix: '-aurora',
	}),
	...generateJobs({
		product: 'contestacao',
		productSetupId: 'ps-aurora-trabalhista-contestacao',
		idPrefix: 'job-aur-ct',
		firstNumber: 1,
		perWindow: [6, 5, 4],
		recentStatuses: ['Aguardando revisão'],
		failureEvery: 9,
		kinds: CONTESTACAO_KINDS,
		seedSuffix: '-aurora',
	}),
	...generateJobs({
		product: 'basilisk',
		productSetupId: 'ps-pampa-civel-basilisk',
		idPrefix: 'job-pam-bs',
		firstNumber: 1,
		perWindow: [7, 6, 4],
		recentStatuses: ['Aguardando revisão', 'Em revisão'],
		failureEvery: 10,
		kinds: BASILISK_KINDS,
		seedSuffix: '-pampa',
	}),
	...generateJobs({
		product: 'cadastro',
		productSetupId: 'ps-pampa-trabalhista-cadastro',
		idPrefix: 'job-pam-cd',
		firstNumber: 1,
		perWindow: [8, 7, 5],
		recentStatuses: [],
		failureEvery: 13,
		kinds: CADASTRO_KINDS,
		seedSuffix: '-pampa',
	}),
];

export const MOCK_JOB_ROSTER: MockJobRosterEntry[] = [
	...HANDWRITTEN_JOBS,
	...GENERATED_JOBS,
	...OTHER_CLIENTS_JOBS,
];

export function rosterEntry(jobId: string): MockJobRosterEntry | undefined {
	return MOCK_JOB_ROSTER.find((entry) => entry.id === jobId);
}
